'use client';

import { cn } from '@utils/cn';
import type { GridStoryblok } from '@utils/storyblok/storyblok';
import { storyblokEditable } from '@utils/storyblok/storyblokEditable';
import { ClientStoryblokComponent } from '@common/storyblok/ClientStoryblokComponent';

/** Color → background + foreground mapping aligned with global.css tokens:
 *  primary   = #726a64  (warm taupe)
 *  secondary = #6f6b4f  (accessible olive)
 *  tertiary  = #4f4944  (dark charcoal — the CSS `accent` token)
 */
const colorBgClass: Record<
  Extract<
    Exclude<GridStoryblok['backgroundColor'], '' | undefined>,
    'primary' | 'secondary' | 'tertiary' | 'background'
  >,
  string
> = {
  primary: 'bg-primary text-light',
  secondary: 'bg-secondary text-light',
  tertiary: 'bg-accent text-light',
  background: 'bg-muted',
};

/** Map a split variant name to its base color key */
const splitColorMap: Record<
  Extract<
    Exclude<GridStoryblok['backgroundColor'], '' | undefined>,
    'primary-split' | 'secondary-split' | 'tertiary-split' | 'background-split'
  >,
  string
> = {
  'primary-split': 'primary',
  'secondary-split': 'secondary',
  'tertiary-split': 'tertiary',
  'background-split': 'background',
};

const mobileSpanClasses = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12',
} as const;

const tabletSpanClasses = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12',
} as const;

const desktopSpanClasses = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  6: 'lg:col-span-6',
  7: 'lg:col-span-7',
  8: 'lg:col-span-8',
  9: 'lg:col-span-9',
  10: 'lg:col-span-10',
  11: 'lg:col-span-11',
  12: 'lg:col-span-12',
} as const;

function parseColumnSpan(columns: string | undefined, index: number, fallback: number) {
  if (!columns) {
    return fallback;
  }

  const parsedValues = columns
    .split(',')
    .map((value) => Number.parseInt(value.trim(), 10))
    .filter((value) => Number.isFinite(value) && value >= 1 && value <= 12);

  return parsedValues[index] ?? fallback;
}

export function Grid({
  backgroundColor,
  blocks,
  desktopColumns,
  gap,
  mobileColumns,
  tabletColumns,
  ...props
}: GridStoryblok) {
  const normalizedGap =
    typeof gap === 'number' ? gap : typeof gap === 'string' ? Number.parseFloat(gap) : 0;
  const mobileSpans = blocks?.map((_, index) => parseColumnSpan(mobileColumns, index, 12)) ?? [];
  const tabletSpans = blocks?.map((_, index) => parseColumnSpan(tabletColumns, index, 12)) ?? [];
  const desktopSpans = blocks?.map((_, index) => parseColumnSpan(desktopColumns, index, 12)) ?? [];

  // Background color logic
  const isSplit = Boolean(backgroundColor && backgroundColor in splitColorMap);
  const isFullBg = Boolean(backgroundColor && !isSplit && backgroundColor in colorBgClass);
  const splitBaseColor = isSplit ? splitColorMap[backgroundColor as string] : null;

  const canUseEqualColumnsLayout =
    blocks != null &&
    blocks.length > 0 &&
    mobileSpans.every((span) => span === 12) &&
    tabletSpans.every((span) => span === 12) &&
    desktopSpans.every((span) => span === desktopSpans[0]) &&
    desktopSpans.reduce((total, span) => total + span, 0) === 12;

  const desktopColumnCount = canUseEqualColumnsLayout ? 12 / desktopSpans[0]! : null;
  const desktopGridClass =
    desktopColumnCount === 2
      ? 'lg:grid-cols-2'
      : desktopColumnCount === 3
        ? 'lg:grid-cols-3'
        : desktopColumnCount === 4
          ? 'lg:grid-cols-4'
          : null;

  return (
    <div
      {...storyblokEditable(props)}
      className={cn(
        'grid items-stretch',
        canUseEqualColumnsLayout && desktopGridClass
          ? `grid-cols-1 ${desktopGridClass}`
          : 'grid-cols-12',
        // Solid background applied to the entire grid
        isFullBg && colorBgClass[backgroundColor as string],
      )}
      style={normalizedGap > 0 ? { gap: `${normalizedGap * 0.25}rem` } : undefined}
    >
      {blocks?.map((blok, index) => {
        const mobileSpan = mobileSpans[index] as keyof typeof mobileSpanClasses;
        const tabletSpan = tabletSpans[index] as keyof typeof tabletSpanClasses;
        const desktopSpan = desktopSpans[index] as keyof typeof desktopSpanClasses;

        // Split pattern: even index → color, odd index → no background
        const itemBgClass =
          isSplit && splitBaseColor ? (index % 2 === 0 ? colorBgClass[splitBaseColor] : '') : '';

        return (
          <div
            className={cn(
              'min-w-0 w-full max-w-full',
              !canUseEqualColumnsLayout && mobileSpanClasses[mobileSpan],
              !canUseEqualColumnsLayout && tabletSpanClasses[tabletSpan],
              !canUseEqualColumnsLayout && desktopSpanClasses[desktopSpan],
              itemBgClass,
              // Add padding to all items in split mode for consistent spacing
              isSplit && 'p-8 md:p-12',
            )}
            key={blok._uid ?? `${blok.component}-${index}`}
          >
            <ClientStoryblokComponent blok={blok} />
          </div>
        );
      })}
    </div>
  );
}
